import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Button, Card, LoadingSpinner, ErrorMessage } from "@/components/ui";
import {
  driverService,
  CreateDriverData,
} from "@/services/driver.service";
import { userService, type User } from "@/services/user.service";
import { zoneService, type Zone } from "@/services/zone.service";
import { useToastStore } from "@/store/toastStore";

// Schéma de validation Zod
const driverSchema = z.object({
  userId: z.string().min(1, "Le compte utilisateur est obligatoire"),
  isActive: z.boolean().default(true),
  hireDate: z.string().optional(),
  zoneIds: z.array(z.string()).optional().default([]),
});

type DriverFormData = z.infer<typeof driverSchema>;

/**
 * Page de formulaire pour créer ou éditer un livreur
 * Basée sur la maquette driver_personnel_management
 */
export function DriverFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  // Récupérer le livreur si édition
  const {
    data: driver,
    isLoading: driverLoading,
    error: driverError,
  } = useQuery({
    queryKey: ["drivers", id],
    queryFn: () => driverService.getById(id!),
    enabled: isEditing,
  });

  const { data: zones = [] } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(true),
  });

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      userId: "",
      isActive: true,
      hireDate: "",
      zoneIds: [] as string[],
    },
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users", "drivers-link"],
    queryFn: () => userService.getAll(true),
  });
  const driverUsers = users.filter((u) => u.role === "DRIVER");

  // Remplir le formulaire avec les données du livreur si édition
  useEffect(() => {
    if (driver) {
      // Formater la date pour l'input type="date" (YYYY-MM-DD)
      const formattedHireDate = driver.hireDate
        ? new Date(driver.hireDate).toISOString().split("T")[0]
        : "";

      reset({
        userId: driver.userId ?? "",
        isActive: driver.isActive,
        hireDate: formattedHireDate,
        zoneIds: driver.zones?.map((z) => z.id) ?? [],
      });
    }
  }, [driver, reset]);

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateDriverData) => driverService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      success("Livreur créé avec succès");
      navigate("/drivers");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message || "Erreur lors de la création du livreur",
      );
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateDriverData>;
    }) => driverService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      queryClient.invalidateQueries({ queryKey: ["drivers", id] });
      success("Livreur modifié avec succès");
      navigate("/drivers");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la modification du livreur",
      );
    },
  });

  const onSubmit = (data: DriverFormData) => {
    const driverData: CreateDriverData = {
      userId: data.userId,
      isActive: data.isActive,
      hireDate: data.hireDate || undefined,
      zoneIds: data.zoneIds?.length ? data.zoneIds : undefined,
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: driverData });
    } else {
      createMutation.mutate(driverData);
    }
  };

  const isLoading = isEditing && driverLoading;
  const mutationError = createMutation.error || updateMutation.error;

  if (isLoading) {
    return (
      <LoadingSpinner
        message={isEditing ? "Chargement du livreur..." : "Chargement..."}
      />
    );
  }

  if (isEditing && driverError) {
    return <ErrorMessage message="Erreur lors du chargement du livreur." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Titre */}
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {isEditing ? "Modifier le livreur" : "Nouveau livreur"}
      </Typography>

      {/* Formulaire */}
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {mutationError && (
            <ErrorMessage
              message={
                (mutationError as any)?.response?.data?.message ||
                "Une erreur est survenue lors de la sauvegarde."
              }
            />
          )}

          <Grid container spacing={3}>
            {/* Compte utilisateur (rôle DRIVER) */}
            <Grid item xs={12}>
              <Controller
                name="userId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={driverUsers}
                    value={driverUsers.find((u) => u.id === field.value) ?? null}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option: User) =>
                      [option.firstName, option.lastName].filter(Boolean).join(" ").trim() ||
                      option.email
                    }
                    onChange={(_, newValue: User | null) => field.onChange(newValue?.id ?? "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Utilisateur livreur"
                        placeholder="Sélectionner un utilisateur DRIVER"
                        error={!!errors.userId}
                        helperText={errors.userId?.message ?? "Le profil livreur est désormais rattaché au compte utilisateur"}
                        required
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Date d'embauche */}
            <Grid item xs={12} md={6}>
              <Controller
                name="hireDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date d'embauche"
                    fullWidth
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.hireDate}
                    helperText={errors.hireDate?.message}
                  />
                )}
              />
            </Grid>

            {/* Zones affectées */}
            <Grid item xs={12}>
              <Controller
                name="zoneIds"
                control={control}
                render={({ field }) => {
                  const selectedZones = zones.filter((z) =>
                    field.value.includes(z.id),
                  );
                  return (
                    <Autocomplete
                      multiple
                      options={zones}
                      value={selectedZones}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? zones.find((z) => z.id === option)?.name ?? ""
                          : option.name
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, newValue: Zone[]) => {
                        field.onChange(newValue.map((z) => z.id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Zones affectées"
                          placeholder="Sélectionner une ou plusieurs zones"
                          helperText="Zones livrées par défaut par ce livreur"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.name}
                            size="small"
                          />
                        ))
                      }
                    />
                  );
                }}
              />
            </Grid>

            {/* Statut actif */}
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Livreur actif"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              variant="text"
              onClick={() => navigate("/drivers")}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEditing ? "Enregistrer les modifications" : "Créer le livreur"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
